/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Modal, Flex, Button } from "antd";
import { useReactToPrint } from "react-to-print";
import { PiPrinterFill } from "react-icons/pi";
import logo from "../../../assets/images/logo_nsf.png";
import DeliveryNoteService from "../../../service/ERP/DeliveryNote.service";
import dayjs from "dayjs";
import "./del.css";

const delService = DeliveryNoteService();

export default function DelPrintPreview() {
  const { code } = useParams();
  const wrapRef  = useRef(null);
  const docRef   = useRef(null);
  const printRef = useRef(null);
  const [header, setHeader] = useState({});
  const [details, setDetails] = useState([]);

  const handlePrint = useReactToPrint({
    documentTitle: `DEL-${code}`,
    removeAfterPrint: true,
  });

  /* ── scale ตาม width ให้ดูใหญ่ขึ้น ── */
  useEffect(() => {
    const fit = () => {
      const wrap   = wrapRef.current;
      const doc    = docRef.current;
      const layout = printRef.current;
      if (!wrap || !doc || !layout) return;
      const availW = wrap.clientWidth - 32;
      const scale  = Math.min(availW / layout.offsetWidth, 1) * 0.85;
      doc.style.transform       = `scale(${scale})`;
      doc.style.transformOrigin = "top center";
      doc.style.marginBottom    = `${(scale - 1) * layout.offsetHeight}px`;
    };
    fit();
    window.addEventListener("resize", fit);
    return () => window.removeEventListener("resize", fit);
  }, [header]);

  useEffect(() => {
    delService.get(code).then((res) => {
      const { header: h, detail: d } = res.data?.data || {};
      setHeader(h || {});
      setDetails(d || []);
    }).catch((err) => {
      Modal.error({
        title: "Error",
        content: err.response?.data?.message || "Request failed. Please try again.",
        onOk: () => window.close(),
      });
    });
  }, [code]);

  const totalCarton = details.reduce((a, d) => a + Number(d.carton || 0), 0);

  return (
    <div className="page-show" id="del" ref={wrapRef}>
      <div className="doc-wrapper" ref={docRef}>
        <div className="print-btn no-print">
          <Button
            className="bn-center bg-blue-400"
            icon={<PiPrinterFill style={{ fontSize: "1.1rem" }} />}
            onClick={() => handlePrint(null, () => wrapRef.current)}
          >
            PRINT
          </Button>
        </div>

        <div className="print-layout" ref={printRef}>

        {/* ── Company Letterhead ── */}
        <div className="print-head">
          <img src={logo} alt="logo" className="head-logo" />
          <div className="head-company">
            <p className="co-name">NINE STAR FOOD CO., LTD.</p>
            <p className="co-addr">99/9 Moo8, Tambon Kongdin, Amphoe Klaeng, Rayong 22160</p>
          </div>
        </div>

        {/* ── Title + Info ── */}
        <div className="print-head-content">
          <div className="title-band">
            <div className="title-center">
              <p className="doc-name">ใบส่งมอบสินค้าเพื่อส่งออก</p>
              <p className="doc-name-en">EXPORT DELIVERY NOTE</p>
            </div>
            <div className="doc-no-box">
              <span className="doc-no-label">เลขที่เอกสาร</span>
              <span className="doc-no-value">{header.delcode ?? "-"}</span>
            </div>
          </div>
          <div className="info-grid">
            <div className="info-left">
              <div className="h-group-text">
                <span className="h-label">ลูกค้า</span>
                <span className="h-text">{header.cusname}</span>
              </div>
              <div className="h-group-text">
                <span className="h-label">Brand</span>
                <span className="h-text">{header.brand}</span>
              </div>
              <div className="h-group-text">
                <span className="h-label">หมายเหตุ</span>
                <span className="h-text" style={{ fontWeight: 400 }}>{header.remark}</span>
              </div>
            </div>
            <div className="info-right">
              <div className="h-group-text">
                <span className="h-label">Job Order</span>
                <span className="h-text">{header.jocode}</span>
              </div>
              <div className="h-group-text">
                <span className="h-label">โหลดที่</span>
                <span className="h-text">{header.load_no != null ? `โหลดที่ ${header.load_no}` : "-"}</span>
              </div>
              <div className="h-group-text">
                <span className="h-label">วันที่ส่งมอบ</span>
                <span className="h-text">
                  {header.dlvdate ? dayjs(header.dlvdate).format("DD/MM/YYYY") : "-"}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* ── Detail Table ── */}
        <div className="print-content">
          <table id="tb-detail">
            <colgroup>
              <col style={{ width: "10mm" }} />
              <col style={{ width: "28mm" }} />
              <col />
              <col style={{ width: "24mm" }} />
              <col style={{ width: "24mm" }} />
              <col style={{ width: "22mm" }} />
              <col style={{ width: "28mm" }} />
            </colgroup>
            <thead>
              <tr>
                <th>ลำดับ</th>
                <th>รหัสสินค้า</th>
                <th style={{ textAlign: "left", paddingLeft: "3mm" }}>รายการสินค้า</th>
                <th>วันที่ผลิต</th>
                <th>วันที่หมดอายุ</th>
                <th>จำนวน/กล่อง</th>
                <th>หมายเหตุ</th>
              </tr>
            </thead>
            <tbody>
              {details.map((d, i) => (
                <tr key={i}>
                  <td className="txt-c">{d.seq ?? i + 1}</td>
                  <td className="txt-c">{d.pg_code}</td>
                  <td className="txt-l">{d.pg_name}</td>
                  <td className="txt-c">
                    {d.production_date ? dayjs(d.production_date).format("DD/MM/YYYY") : "-"}
                  </td>
                  <td className="txt-c">
                    {d.expiry_date ? dayjs(d.expiry_date).format("DD/MM/YYYY") : "-"}
                  </td>
                  <td className="txt-r">{Number(d.carton || 0).toLocaleString()}</td>
                  <td className="txt-l" style={{ fontSize: "8pt", whiteSpace: "pre-line" }}>{d.detail_remark}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <th className="txt-c" colSpan={5}>รวม</th>
                <th className="txt-r">{totalCarton.toLocaleString()}</th>
                <th>{" "}</th>
              </tr>
            </tfoot>
          </table>
        </div>

        {/* ── Signatures ── */}
        <div className="print-foot">
          <Flex gap="2rem" style={{ paddingTop: "5mm" }}>
            <Flex vertical style={{ width: "50%" }} gap="1.3rem">
              <div className="h-group-text">
                <span className="h-label txt-r" style={{ width: "36mm", flexWrap: "nowrap" }}>
                  ผู้ส่งมอบ
                </span>
                <span className="h-text txt-c unline-row">{header.created_name ?? " "}</span>
              </div>
              <div className="h-group-text">
                <span className="h-label txt-r" style={{ width: "36mm", flexWrap: "nowrap" }}>
                  วันที่
                </span>
                <span className="h-text txt-c unline-row">{" "}</span>
              </div>
            </Flex>
            <Flex vertical style={{ width: "50%" }} gap="1.3rem">
              <div className="h-group-text">
                <span className="h-label txt-r" style={{ width: "40mm", flexWrap: "nowrap", justifyContent: "end" }}>
                  ผู้รับมอบ
                </span>
                <span className="h-text txt-c unline-row">{" "}</span>
              </div>
              <div className="h-group-text">
                <span className="h-label txt-r" style={{ width: "40mm", flexWrap: "nowrap", justifyContent: "end" }}>
                  วันที่
                </span>
                <span className="h-text txt-c unline-row">{" "}</span>
              </div>
            </Flex>
          </Flex>
        </div>

        </div>
      </div>
    </div>
  );
}
